import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, ShieldCheck, UserPlus, Mail } from 'lucide-react';
import Modal from '../components/Modal';
import { memberRole } from '../components/AdminMemberDetails';
import { auditActionLabel, formatDateTime, staffRoleLabel } from '../lib/permissions';
import '../styles/adminResponsive.css';
import '../styles/adminMembers.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVITE_STATUS = { created: 'Oluşturuldu', used: 'Kullanıldı', revoked: 'İptal', expired: 'Süresi doldu' };
const MAIL_STATUS = { accepted: 'E-posta gönderildi', failed: 'E-posta gönderilemedi', not_configured: 'E-posta ayarı yok', pending: 'Gönderiliyor', not_sent: '—' };
const errorText = (err, fallback) => (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : fallback);
const accountLabel = member => member.role === 'pool_manager' ? 'Yalnız personel hesabı' : memberRole(member.role);

function useList(path, key = 'items') {
  const [state, setState] = useState({ items: [], loading: true, error: '', extra: {} });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let alive = true;
    setState(s => ({ ...s, loading: true, error: '' }));
    axios.get(`${API}${path}`, { timeout: 12000 })
      .then(({ data }) => { if (alive) setState({ items: data?.[key] || [], loading: false, error: '', extra: data || {} }); })
      .catch(() => { if (alive) setState(s => ({ ...s, loading: false, error: 'Liste yüklenemedi. Yeniden deneyin.' })); });
    return () => { alive = false; };
  }, [path, key, revision]);
  return { ...state, reload: () => setRevision(r => r + 1) };
}

function AssignMember({ onAssigned }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ items: [], loading: false, error: '' });
  const [busy, setBusy] = useState('');
  useEffect(() => {
    if (q.trim().length < 2) { setResults({ items: [], loading: false, error: '' }); return undefined; }
    let alive = true;
    setResults(r => ({ ...r, loading: true, error: '' }));
    const timer = setTimeout(() => {
      axios.get(`${API}/admin/users`, { params: { q: q.trim(), limit: 10 }, timeout: 12000 })
        .then(({ data }) => { if (alive) setResults({ items: data.items || [], loading: false, error: '' }); })
        .catch(() => { if (alive) setResults({ items: [], loading: false, error: 'Üyeler aranamadı. Yeniden deneyin.' }); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [q]);
  const assign = async member => {
    if (busy) return;
    setBusy(member.id);
    try {
      const { data } = await axios.post(`${API}/admin/staff/assign`, { user_id: member.id }, { timeout: 15000 });
      toast.success(data.already_assigned ? 'Bu üye zaten personel.' : `${member.name || member.email} Havuz Yöneticisi oldu.`);
      setResults(r => ({ ...r, items: r.items.map(i => i.id === member.id ? { ...i, staff_role: 'pool_manager' } : i) }));
      onAssigned();
    } catch (err) { toast.error(errorText(err, 'Personel rolü verilemedi.')); }
    finally { setBusy(''); }
  };
  return <section className="admin-member-card admin-member-panel" aria-labelledby="staff-assign-title" data-testid="staff-assign">
    <h2 id="staff-assign-title" className="font-semibold mb-2 flex items-center gap-2"><UserPlus size={18} aria-hidden="true" />Mevcut üyeyi ata</h2>
    <p className="text-sm mb-3">Kayıtlı bir mekân veya organizatör hesabına Havuz Yöneticisi yetkisi ver. Kişi kendi üyeliğini kullanmaya devam eder.</p>
    <label className="block text-sm">Ad veya e-posta
      <input className="admin-member-input" type="search" value={q} maxLength={120} placeholder="En az 2 harf yaz"
        onChange={e => setQ(e.target.value)} data-testid="staff-assign-search" /></label>
    {results.loading && <p role="status" className="mt-3">Aranıyor…</p>}
    {results.error && <p role="alert" className="mt-3 admin-member-error">{results.error}</p>}
    {!results.loading && q.trim().length >= 2 && !results.error && !results.items.length && <p className="mt-3">Bu aramaya uygun üye yok.</p>}
    <ul className="admin-staff-list mt-3">
      {results.items.map(member => <li key={member.id} className="admin-staff-row">
        <div><strong>{member.name || 'İsimsiz hesap'}</strong><p className="admin-member-email">{member.email}</p><small>{accountLabel(member)}</small></div>
        {member.role === 'admin' ? <span className="text-sm">Ana yönetici</span>
          : member.staff_role ? <span className="status-pill">Personel</span>
            : <button type="button" className="admin-member-button" disabled={!!busy} onClick={() => assign(member)}
              data-testid={`staff-assign-${member.id}`}>{busy === member.id ? 'Atanıyor…' : 'Havuz Yöneticisi yap'}</button>}
      </li>)}
    </ul>
  </section>;
}

function InviteForm({ onCreated }) {
  const [form, setForm] = useState({ email: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submitting = useRef(false);
  const submit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/staff/invites`, { email: form.email.trim(), name: form.name.trim() }, { timeout: 20000 });
      const link = data.invite_link || `${window.location.origin}${data.invite_path}`;
      setResult({ link, mailed: !data.manual_share_required, status: data.email_status });
      setForm({ email: '', name: '' });
      onCreated();
    } catch (err) { toast.error(errorText(err, 'Davet oluşturulamadı.')); }
    finally { submitting.current = false; setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(result.link); toast.success('Davet bağlantısı kopyalandı.'); }
    catch { toast.error('Kopyalanamadı. Bağlantıyı seçip elle kopyalayın.'); }
  };
  return <section className="admin-member-card admin-member-panel" aria-labelledby="staff-invite-title" data-testid="staff-invite">
    <h2 id="staff-invite-title" className="font-semibold mb-2 flex items-center gap-2"><Mail size={18} aria-hidden="true" />E-posta ile davet et</h2>
    <p className="text-sm mb-3">Yeni bir kişi için yalnız personel işlerinde kullanılacak bir hesap açtır. Bağlantı 7 gün geçerli ve tek kullanımlıktır.</p>
    <form onSubmit={submit} className="admin-member-delete-form">
      <label className="text-sm">Ad soyad<input className="admin-member-input" value={form.name} required minLength={2} maxLength={120}
        onChange={e => setForm({ ...form, name: e.target.value })} data-testid="staff-invite-name" /></label>
      <label className="text-sm">E-posta<input className="admin-member-input" type="email" value={form.email} required maxLength={320}
        onChange={e => setForm({ ...form, email: e.target.value })} data-testid="staff-invite-email-input" /></label>
      <div className="admin-member-actions"><button type="submit" className="action-primary" disabled={busy} data-testid="staff-invite-submit">
        {busy ? 'Oluşturuluyor…' : 'Davet oluştur'}</button></div>
    </form>
    {result && <div className="admin-staff-link mt-4" role="status" data-testid="staff-invite-result">
      <p className="text-sm mb-2">{result.mailed ? 'Davet e-postası gönderildi. Gerekirse bağlantıyı ayrıca paylaşabilirsin:'
        : 'E-posta gönderilemedi. Bu bağlantıyı kişiye güvenli bir kanaldan elle ilet:'}</p>
      <code className="admin-staff-code" data-testid="staff-invite-link">{result.link}</code>
      <div className="admin-member-actions mt-2"><button type="button" className="admin-member-button" onClick={copy} data-testid="staff-invite-copy">
        <Copy size={14} aria-hidden="true" /> Bağlantıyı kopyala</button></div>
      <p className="text-xs mt-2">Bu bağlantı yalnız şimdi gösterilir; sayfadan çıkınca tekrar görüntülenemez.</p>
    </div>}
  </section>;
}

function RevokeDialog({ member, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async event => {
    event.preventDefault();
    if (busy || reason.trim().length < 3) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/admin/staff/revoke`, { user_id: member.id, reason: reason.trim() }, { timeout: 15000 });
      toast.success('Personel yetkisi kaldırıldı; açık oturumları kapatıldı.');
      onDone();
    } catch (err) { setError(errorText(err, 'Yetki kaldırılamadı. Yeniden deneyin.')); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={() => { if (!busy) onClose(); }} title="Personel yetkisini kaldır" testId="staff-revoke-modal">
    <form onSubmit={submit} className="admin-member-delete-form">
      <p><strong>{member.name}</strong><br /><span className="admin-member-email">{member.email}</span></p>
      <p>{member.staff_only ? 'Bu hesap yalnız personel hesabı. Yetki kaldırılınca hesapla hiçbir işlem yapılamaz.'
        : 'Kişinin üyeliği devam eder; yalnız personel yetkisi kalkar.'} Tüm açık oturumları hemen kapanır.</p>
      <label className="admin-member-confirm-label">Gerekçe (kayıtta saklanır)
        <textarea className="admin-member-input" rows={3} value={reason} maxLength={500} required minLength={3}
          onChange={e => setReason(e.target.value)} disabled={busy} data-testid="staff-revoke-reason" /></label>
      {error && <p role="alert" className="admin-member-error">{error}</p>}
      <div className="admin-member-actions">
        <button type="submit" className="admin-member-button admin-member-danger" disabled={busy || reason.trim().length < 3} data-testid="staff-revoke-confirm">
          {busy ? 'Kaldırılıyor…' : 'Yetkiyi kaldır'}</button>
        <button type="button" className="admin-member-button" onClick={onClose} disabled={busy}>Vazgeç</button>
      </div>
    </form>
  </Modal>;
}

export default function AdminStaff() {
  const staff = useList('/admin/staff');
  const invites = useList('/admin/staff/invites');
  const audit = useList('/admin/audit?limit=30');
  const [revoking, setRevoking] = useState(null);
  const refresh = () => { staff.reload(); invites.reload(); audit.reload(); };
  const revokeInvite = async invite => {
    if (!window.confirm(`${invite.email} için daveti iptal etmek istiyor musun?`)) return;
    try { await axios.post(`${API}/admin/staff/invites/${encodeURIComponent(invite.id)}/revoke`, {}, { timeout: 15000 }); toast.success('Davet iptal edildi.'); refresh(); }
    catch (err) { toast.error(errorText(err, 'Davet iptal edilemedi.')); }
  };
  const staffNames = Object.fromEntries(staff.items.map(s => [s.id, s.name || s.email]));
  return <section className="p-8 max-w-7xl mx-auto admin-directory-page" data-testid="admin-staff-page">
    <div className="mb-6"><h1 className="text-3xl font-semibold flex items-center gap-2"><ShieldCheck size={28} aria-hidden="true" />Ekip ve Yetkiler</h1>
      <p className="mt-2">Havuz Yöneticileri dış kayıtları, profil incelemelerini ve şikâyetleri yönetir. Üyelik, ödeme, rozet ve sistem ayarları yalnız sende kalır.</p>
      {staff.extra.enabled === false && <p role="status" className="home-notice mt-3">Personel hesapları şu anda kapalı (POOL_MANAGER_ENABLED). Personel izinleri geçici olarak çalışmaz.</p>}
    </div>

    <section className="mb-8" aria-labelledby="staff-list-title">
      <h2 id="staff-list-title" className="text-xl font-semibold mb-3">Personel</h2>
      {staff.loading ? <p role="status">Yükleniyor…</p> : staff.error ? <div role="alert"><p>{staff.error}</p><button className="admin-member-button" onClick={staff.reload}>Tekrar dene</button></div>
        : !staff.items.length ? <p>Henüz personel yok. Aşağıdan mevcut bir üyeyi atayabilir veya e-posta ile davet edebilirsin.</p>
          : <div className="admin-members-list" data-testid="staff-list">{staff.items.map(member => <article key={member.id} className="admin-member-card">
            <div className="admin-member-summary">
              <div><h3 className="font-semibold">{member.name || 'İsimsiz hesap'}</h3><p className="admin-member-email">{member.email}</p>
                <p className="text-sm">{accountLabel(member)} · {staffRoleLabel(member.staff_role)}</p></div>
              <div className="text-sm"><p>Atanma: {formatDateTime(member.granted_at)}{member.granted_by?.name ? ` · ${member.granted_by.name}` : ''}</p>
                <p>Son işlem: {formatDateTime(member.last_action_at)}</p></div>
              <button type="button" className="admin-member-button admin-member-danger" onClick={() => setRevoking(member)} data-testid={`staff-revoke-${member.id}`}>Yetkiyi kaldır</button>
            </div>
          </article>)}</div>}
    </section>

    <div className="admin-staff-grid mb-8">
      <AssignMember onAssigned={refresh} />
      <InviteForm onCreated={refresh} />
    </div>

    <section className="mb-8" aria-labelledby="staff-invites-title">
      <h2 id="staff-invites-title" className="text-xl font-semibold mb-3">Personel davetleri</h2>
      {invites.loading ? <p role="status">Yükleniyor…</p> : invites.error ? <p role="alert">{invites.error}</p>
        : !invites.items.length ? <p>Henüz personel daveti yok.</p>
          : <ul className="admin-staff-list" data-testid="staff-invite-list">{invites.items.map(invite => <li key={invite.id} className="admin-staff-row admin-member-card">
            <div><strong>{invite.name}</strong><p className="admin-member-email">{invite.email}</p>
              <small>Oluşturma: {formatDateTime(invite.created_at)} · Son geçerlilik: {formatDateTime(invite.expires_at)}{invite.used_at ? ` · Kullanım: ${formatDateTime(invite.used_at)}` : ''}</small></div>
            <div className="admin-member-actions">
              <span className="status-pill" data-testid={`staff-invite-status-${invite.id}`}>{INVITE_STATUS[invite.status] || invite.status}</span>
              <small>{MAIL_STATUS[invite.email_status] || invite.email_status}</small>
              {invite.status === 'created' && <button type="button" className="admin-member-button" onClick={() => revokeInvite(invite)} data-testid={`staff-invite-revoke-${invite.id}`}>İptal et</button>}
            </div>
          </li>)}</ul>}
    </section>

    <section aria-labelledby="staff-audit-title">
      <h2 id="staff-audit-title" className="text-xl font-semibold mb-3">Faaliyet kaydı</h2>
      {audit.loading ? <p role="status">Yükleniyor…</p> : audit.error ? <p role="alert">{audit.error}</p>
        : !audit.items.length ? <p>Henüz kayıtlı işlem yok.</p>
          : <div className="admin-member-card admin-member-panel"><ul className="admin-staff-activity" data-testid="staff-audit-list">{audit.items.map(entry => <li key={entry.id}>
            <strong>{auditActionLabel(entry.action)}</strong>
            <span>{entry.actor_role === 'admin' ? 'Ana yönetici' : staffNames[entry.actor_id] || 'Personel'} · {formatDateTime(entry.at)}</span>
            {entry.reason && <small>Gerekçe: {entry.reason}</small>}
          </li>)}</ul></div>}
    </section>
    {revoking && <RevokeDialog member={revoking} onClose={() => setRevoking(null)} onDone={() => { setRevoking(null); refresh(); }} />}
  </section>;
}
